import { useParams } from "react-router-dom";
import useAxios from "../../../../../../hooks/useAxios";
import useLoading from "../../../../../../shared/components/loading/useLoading";
import {
  OBJECTIVE_TRACKER_BY_ID,
  OBJECTIVE_TRACKER_PERIODS,
  OBJECTIVES_TRACKER_DIMENSION_SELECTABLES,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import { useEffect, useState } from "react";
import { catchFunction } from "../../../../../../shared/lib/utils";
import BreadcrumbPanel from "../../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import {
  OBJECTIVES_TRACKER,
  TOOLS,
} from "../../../../../../shared/lib/routesResolver";
import ObjectivesTrackerDetail from "./ObjectivesTrackerDetail";
import ObjectivesTrackerPeriod from "./ObjectivesTrackerPeriod";
import useToastWrapper from "../../../../../../hooks/useToastWrapper";
import { useTranslation } from "react-i18next";
import useConfirmDialog from "../../../../../../shared/components/confirm-dialog/useConfirmDialog";
import { useNavigate } from "react-router";

const ObjectivesTrackerDetailContainer = () => {
  const { id } = useParams();
  const { setLoading } = useLoading();
  const axios = useAxios();

  const [tracker, setTracker] = useState();
  const [periods, setPeriods] = useState([]);
  const [dimensionSelectables, setDimensionSelectables] = useState([]);
  const toast = useToastWrapper();
  const { t } = useTranslation();
  const deleteTrackerDialog = useConfirmDialog();
  const navigate = useNavigate();

  const breadcrumbItems = [
    { title: "objectivesTracker.detail.breadcrumb.first", link: TOOLS },
    {
      title: "objectivesTracker.detail.breadcrumb.second",
      link: OBJECTIVES_TRACKER,
    },
    {
      title: "objectivesTracker.detail.breadcrumb.third",
      titleOptions: { name: tracker?.name },
    },
  ];

  const findTracker = () => {
    axios
      .get(OBJECTIVE_TRACKER_BY_ID(id))
      .then((response) => setTracker(response.data))
      .catch(catchFunction);
  };

  const findTrackerPeriods = () => {
    setLoading(true);
    axios
      .get(OBJECTIVE_TRACKER_PERIODS(id))
      .then((response) => setPeriods(response.data))
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const findDimensionSelectables = () => {
    if (tracker?.client?.id) {
      axios
        .get(OBJECTIVES_TRACKER_DIMENSION_SELECTABLES(tracker?.client?.id))
        .then((response) => setDimensionSelectables(response.data))
        .catch(catchFunction);
    }
  };

  const onPeriodCreation = (formValues) => {
    const request = {
      dateRange: {
        from: formValues.beginDate,
        to: formValues.endDate,
      },
      goal: formValues.goal,
    };
    setLoading(true);
    axios
      .post(OBJECTIVE_TRACKER_PERIODS(id), request)
      .then((response) => {
        toast.success(
          t("objectivesTracker.createPeriodForm.success", {
            from: response.data.beginDate,
            to: response.data.endDate,
          }),
        );
        addPeriod(response.data);
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const removePeriod = (periodId) => {
    const periodsCopy = [...periods];
    const result = periodsCopy.filter((p) => p.id !== periodId);
    setPeriods(result);
  };

  const updatePeriod = (period) => {
    const periodsCopy = [...periods];
    const index = periods.findIndex((p) => p.id === period.id);
    periodsCopy[index] = period;
    setPeriods(periodsCopy);
  };

  const addPeriod = (period) => {
    setPeriods([...periods, period]);
  };

  const onTrackerEdition = (formValues) => {
    setLoading(true);
    const request = {
      name: formValues.name,
      goal: formValues.goal,
      dateRange: {
        from: formValues.beginDate,
        to: formValues.endDate,
      },
    };
    axios
      .put(OBJECTIVE_TRACKER_BY_ID(tracker.id), request)
      .then((response) => {
        toast.success(
          t("objectivesTracker.editForm.success", { name: response.name }),
        );
        findTracker();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onTrackerDeletion = () => {
    deleteTrackerDialog.display(`tracker ${tracker.name}`);
  };

  const deleteTracker = () => {
    setLoading(true);
    axios
      .delete(OBJECTIVE_TRACKER_BY_ID(tracker.id))
      .then((_) => {
        toast.success(
          t("objectivesTracker.deleteTracker", { name: tracker.name }),
        );
        navigate(OBJECTIVES_TRACKER);
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    findTracker();
    findTrackerPeriods();
  }, [id]);

  useEffect(() => {
    findDimensionSelectables();
  }, [tracker]);

  return (
    <>
      {deleteTrackerDialog.renderSafeDialog(deleteTracker)}
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 ">
        <ObjectivesTrackerDetail
          tracker={tracker}
          onPeriodCreation={onPeriodCreation}
          onTrackerEdition={onTrackerEdition}
          onTrackerDeletion={onTrackerDeletion}
        />
        {periods &&
          periods.map((period, index) => (
            <ObjectivesTrackerPeriod
              key={period.id}
              index={0}
              periodNumber={index}
              trackerId={id}
              trackerGoal={tracker?.result?.goal}
              period={period}
              selectables={dimensionSelectables}
              removePeriodFromParent={removePeriod}
              updatePeriodFromParent={updatePeriod}
              addPeriodToParent={addPeriod}
            />
          ))}
      </main>
    </>
  );
};

export default ObjectivesTrackerDetailContainer;
